<template>
  <div class="w-full">
    <div
      class="choice-wrapper gap-3"
      :class="layoutStyle.wrapper.class"
      :style="layoutStyle.wrapper.style">
      <transition-group name="choice-item" tag="div" class="contents">
        <div
          v-for="(item, index) in items"
          :key="item.id"
          class="transition-all relative choice-item"
          :class="layoutStyle.item(index).class"
          :style="layoutStyle.item(index).style">
          <slot
            name="item"
            :item="item"
            :selected="item.selected"
            :index="index"
            :exceeded-max-selections="countOptionsSelected >= max"
            :allow-delete="items.length > 2"></slot>
          <div
            v-if="index === items.length - 1 && items.length < maxItems"
            class="absolute flex items-center justify-center"
            :class="layoutStyle.addItem.class">
            <slot v-if="hasSlot('add-item')" name="add-item" />
          </div>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type PropType, useSlots, computed, ref, watch, provide } from 'vue';
import type { LayoutType, FitType, ChoiceOptionType } from '@amit/types';
import { KeyboardSettings } from '@amit/types';

const slots = useSlots();

// Track stable item count for layout calculations during transitions
const stableItemCount = ref(0);

const props = defineProps({
  type: {
    type: String as PropType<'text' | 'card' | 'survey' | 'post'>,
    default: 'text',
  },
  display: {
    type: String as PropType<'grid' | 'flex'>,
    default: 'grid',
  },
  layout: {
    type: String as PropType<LayoutType>,
    default: 'horizontal',
  },
  fit: {
    type: String as PropType<FitType>,
    default: 'full',
  },
  options: {
    type: Array as PropType<ChoiceOptionType[]>,
    default: () => [],
  },
  maxHorizontalItems: {
    type: Number,
    default: 4,
  },
  maxVerticalItems: {
    type: Number,
    default: 6,
  },
  justify: {
    type: String as PropType<'start' | 'center' | 'end'>,
    default: 'center',
  },
  shuffle: {
    type: Boolean,
    default: false,
  },
  min: {
    type: Number,
    default: 1,
  },
  max: {
    type: Number,
    default: 4,
  },
  rtl: {
    type: Boolean,
    default: true,
  },
  keyboardSettings: {
    type: Object as PropType<KeyboardSettings>,
    default: () => ({
      showMathmaticalKeyboard: false,
      keyboardList: [],
    }),
  },
});
provide(
  'keyboardSettings',
  computed(() => props.keyboardSettings),
);
const _emit = defineEmits(['add-option', 'delete-option']);

// Shuffle function
const shuffleArray = (array: ChoiceOptionType[]) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Convert items to a computed property
const items = computed(() => {
  // Separate editable and non-editable options
  const editableOptions = props.options.filter(option => option.editable);
  const nonEditableOptions = props.options.filter(option => !option.editable);

  // Shuffle only non-editable options if shuffle is enabled
  const shuffledNonEditable = props.shuffle
    ? shuffleArray(nonEditableOptions)
    : [...nonEditableOptions];

  // Always append editable options at the end
  return [...shuffledNonEditable, ...editableOptions];
});

// Watch for item count changes and update stable count appropriately
watch(
  () => items.value.length,
  (newLength, oldLength = 0) => {
    if (newLength > stableItemCount.value) {
      // Items added - update immediately
      stableItemCount.value = newLength;
    } else if (newLength < oldLength && newLength < stableItemCount.value) {
      // Items removed - delay update to allow transition to complete
      setTimeout(() => {
        stableItemCount.value = newLength;
      }, 450); // Slightly longer than leave transition duration
    }
  },
  { immediate: true },
);

const maxItems = computed(
  () => props.maxHorizontalItems * props.maxVerticalItems,
);
const countOptionsSelected = computed(
  () => items.value.filter(item => item.selected).length,
);

const hasSlot = (name: string) => {
  return !!slots[name];
};

const justifyClass = computed(() => {
  return {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
  }[props.justify];
});

const layoutStyle = computed(() => {
  if (props.display === 'flex') {
    return {
      wrapper: {
        class: `flex flex-wrap ${justifyClass.value}`,
        style: '',
      },
      item: (index: number) => {
        const itemsPerRow = Math.min(
          items.value.length,
          props.maxHorizontalItems,
        );
        const needsBreak =
          index !== 0 && index % props.maxHorizontalItems === 0;

        // Handle different fit types
        let width;
        if (props.fit === 'full') {
          // Full fit - distribute items to fill the row
          width =
            itemsPerRow > 1
              ? `calc(${100 / itemsPerRow}% - 1.25rem * ${itemsPerRow - 1} / ${itemsPerRow})`
              : '100%';
        } else {
          // Auto fit - each item takes up 1/maxHorizontalItems of the row
          width = `calc(${100 / props.maxHorizontalItems}% - 1.25rem * ${props.maxHorizontalItems - 1} / ${props.maxHorizontalItems})`;
        }

        return {
          class: needsBreak ? 'w-full flex-none' : 'flex-none',
          style: `width: ${width}; max-width: ${width};`,
        };
      },
      addItem: {
        class: !props.rtl
          ? 'top-1/2 -translate-y-1/2 right-0 -mr-7'
          : 'top-1/2 -translate-y-1/2 left-0 -ml-7',
        style: '',
      },
    };
  }

  const layouts = {
    horizontal: {
      wrapper: {
        class: 'grid grid-cols-12',
        style: {},
      },
      item: (index: number) => ({
        class: [
          props.type === 'survey'
            ? 'col-span-1'
            : props.type === 'post'
              ? 'md:col-span-6 col-span-12'
              : props.fit === 'full'
                ? `col-span-${Math.floor(12 / Math.min(items.value.length, props.maxHorizontalItems))}`
                : 'col-span-3',
          index !== 0 && index % props.maxHorizontalItems === 0
            ? 'col-start-1'
            : '',
        ],
        style:
          props.fit === 'full' && props.type !== 'survey'
            ? `grid-column: span ${Math.floor(12 / Math.min(items.value.length, props.maxHorizontalItems))} / span ${Math.floor(12 / Math.min(items.value.length, props.maxHorizontalItems))}`
            : '',
      }),
      addItem: {
        class: !props.rtl
          ? 'top-1/2 -translate-y-1/2 right-0 -mr-7'
          : 'top-1/2 -translate-y-1/2 left-0 -ml-7',
        style: '',
      },
    },
    vertical: {
      wrapper: {
        class: 'grid',
        style: (() => {
          // Use stable count to prevent layout shifts during transitions
          const countToUse = stableItemCount.value || items.value.length;
          const actualRows = Math.min(countToUse, props.maxVerticalItems);
          const hasAddButton =
            items.value.length < maxItems.value && hasSlot('add-item');
          const paddingBottom = hasAddButton ? '2rem' : '0';

          if (props.fit === 'full') {
            // Full fit - use all available space, distribute items across columns
            return `grid-template-rows: repeat(${actualRows}, auto); grid-auto-flow: column; grid-auto-columns: minmax(0, 1fr); padding-bottom: ${paddingBottom};`;
          } else {
            // Auto fit - use minimum space, fixed number of columns
            return `grid-template-rows: repeat(${actualRows}, auto); grid-auto-flow: column; grid-template-columns: repeat(4, minmax(0, 3fr)); padding-bottom: ${paddingBottom};`;
          }
        })(),
      },
      item: () => ({
        class: props.fit === 'full' ? '' : 'w-full break-words',
        style: '',
      }),
      addItem: {
        class: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-8',
        style: '',
      },
    },
  };
  return layouts[props.layout];
});
</script>

<style scoped>
/* Choice item transition animations */
.choice-item-enter-active {
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.choice-item-leave-active {
  transition: all 0.4s cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.choice-item-enter-from {
  opacity: 0;
  transform: scale(0.8) translateY(-10px);
}

.choice-item-move {
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.choice-item-leave-to {
  opacity: 0;
  transform: scale(0.5) rotateZ(5deg);
}
</style>
